// Heist Decks — the environment-reaction mechanic for Heist City.
//
// At the end of every round, the environment reacts to how the crews have been
// operating. Count the total number of characters across both teams in each
// state (Hidden, Disguised, Overt). Draw one card from the deck matching the
// majority state. If two (or three) states tie for the majority, draw one card
// from each tied deck and apply all effects. Cards with lasting effects remain
// active until the next draw from that deck.
//
// Pure data + helpers — no framework dependencies.

import type { CharacterState } from '../../components/games/heist-city/types';

export type HeistDeckId = 'overt' | 'hidden' | 'disguised';

export const HEIST_DECK_IDS: readonly HeistDeckId[] = ['overt', 'hidden', 'disguised'];

export const HEIST_DECK_NAMES: Record<HeistDeckId, string> = {
  overt: 'Overt Deck',
  hidden: 'Hidden Deck',
  disguised: 'Disguised Deck',
};

// The character state each deck is triggered by.
export const DECK_STATE: Record<HeistDeckId, CharacterState> = {
  overt: 'Overt',
  hidden: 'Hidden',
  disguised: 'Disguised',
};

export interface HeistCard {
  id: string;        // e.g. 'overt-3'
  deck: HeistDeckId;
  number: number;    // 1-8, the card's position in its deck's reference list
  name: string;
  effect: string;
}

// One deck's runtime state. The active card stays in its slot (lasting effects
// remain) until the next draw from this deck, at which point it is discarded.
export interface HeistDeckState {
  drawPile: string[];     // card ids, top of deck = index 0
  discardPile: string[];  // card ids
  activeCardId: string | null;
}

export interface HeistDecksState {
  overt: HeistDeckState;
  hidden: HeistDeckState;
  disguised: HeistDeckState;
}

// --- Card definitions (from the Heist City rulebook) ---

export const HEIST_CARDS: Record<HeistDeckId, HeistCard[]> = {
  overt: [
    { id: 'overt-1',  deck: 'overt', number: 1,  name: 'Suppressive Fire',    effect: 'Choose one of your Overt characters. Their attacks deal +2 damage this turn.' },
    { id: 'overt-2',  deck: 'overt', number: 2,  name: 'Take Cover',           effect: 'Move one of your Overt characters up to 2 hexes for free.' },
    { id: 'overt-3',  deck: 'overt', number: 3,  name: 'Rally',                effect: 'Remove 1 wound from one of your Overt characters.' },
    { id: 'overt-4',  deck: 'overt', number: 4,  name: 'Field Medic',          effect: 'Remove 1 wound from each of your Overt characters.' },
    { id: 'overt-5',  deck: 'overt', number: 5,  name: 'Overwatch',            effect: 'One of your Overt characters makes a free attack against the next enemy to move within range 3 this turn.' },
    { id: 'overt-6',  deck: 'overt', number: 6,  name: 'Sprint',               effect: 'One of your Overt characters may move up to double their M stat this turn.' },
    { id: 'overt-7',  deck: 'overt', number: 7,  name: 'Intimidation',         effect: 'Move one enemy character 1 hex in any direction of your choice.' },
    { id: 'overt-8',  deck: 'overt', number: 8,  name: 'Coordinated Assault',  effect: 'Two of your Overt characters each make one free melee attack (MS vs. Defense).' },
    { id: 'overt-9',  deck: 'overt', number: 9,  name: 'Adrenaline',           effect: 'One of your Overt characters gains 1 extra action this turn.' },
    { id: 'overt-10', deck: 'overt', number: 10, name: 'Breach',               effect: 'One of your Overt characters may move through walls this turn.' },
    { id: 'overt-11', deck: 'overt', number: 11, name: 'Suppression',          effect: 'One enemy character of your choice cannot take ranged attacks until the start of your next turn.' },
    { id: 'overt-12', deck: 'overt', number: 12, name: 'Escalation',           effect: 'All of your Overt characters get +1 to their attack rolls this turn.' },
  ],
  hidden: [
    { id: 'hidden-1',  deck: 'hidden', number: 1,  name: 'Shadow Step',        effect: 'Move one of your Hidden characters up to their full M stat for free.' },
    { id: 'hidden-2',  deck: 'hidden', number: 2,  name: 'Ambush',             effect: 'One of your Hidden characters deals +3 damage on their next attack this turn.' },
    { id: 'hidden-3',  deck: 'hidden', number: 3,  name: 'Vanish',             effect: 'Immediately change one of your Overt characters to the Hidden state.' },
    { id: 'hidden-4',  deck: 'hidden', number: 4,  name: 'Ghost',              effect: 'One of your Hidden characters cannot be targeted by any attacks until your next activation.' },
    { id: 'hidden-5',  deck: 'hidden', number: 5,  name: 'Dead Drop',          effect: 'One of your Hidden characters gains 1 VP immediately.' },
    { id: 'hidden-6',  deck: 'hidden', number: 6,  name: 'Infiltrate',         effect: 'One of your Hidden characters ignores terrain and walls when moving this turn.' },
    { id: 'hidden-7',  deck: 'hidden', number: 7,  name: 'Counter-Intel',      effect: 'One of your Hidden characters takes a free Hack action now.' },
    { id: 'hidden-8',  deck: 'hidden', number: 8,  name: 'Extraction',         effect: 'Move one of your Hidden characters up to 4 hexes for free.' },
    { id: 'hidden-9',  deck: 'hidden', number: 9,  name: 'Sabotage',           effect: 'One of your Hidden characters disables one enemy computer or gear within range 2 until the end of your next turn.' },
    { id: 'hidden-10', deck: 'hidden', number: 10, name: 'Blind Spot',         effect: 'Your Hidden characters cannot be targeted by enemy Hack or Grift actions until your next turn.' },
    { id: 'hidden-11', deck: 'hidden', number: 11, name: 'Reposition',         effect: 'Swap the positions of any two of your characters.' },
    { id: 'hidden-12', deck: 'hidden', number: 12, name: 'Silent Sweep',       effect: 'Each of your Hidden characters may move 1 hex for free.' },
  ],
  disguised: [
    { id: 'disguised-1',  deck: 'disguised', number: 1,  name: 'Misdirection',      effect: 'Move one enemy character up to 2 hexes of your choice.' },
    { id: 'disguised-2',  deck: 'disguised', number: 2,  name: 'Social Engineering', effect: 'One of your Disguised characters takes a free Grift action now.' },
    { id: 'disguised-3',  deck: 'disguised', number: 3,  name: 'Distraction',        effect: 'One enemy character of your choice loses their next action.' },
    { id: 'disguised-4',  deck: 'disguised', number: 4,  name: 'Inside Man',         effect: 'One of your Disguised characters gains 1 VP immediately.' },
    { id: 'disguised-5',  deck: 'disguised', number: 5,  name: 'Cover Story',        effect: 'One of your Disguised characters may freely change to any available persona this turn.' },
    { id: 'disguised-6',  deck: 'disguised', number: 6,  name: 'Smooth Operator',    effect: 'Remove 1 wound from one of your Disguised characters.' },
    { id: 'disguised-7',  deck: 'disguised', number: 7,  name: 'The Long Con',       effect: 'Your opponent must discard 1 card of their choice from their hand.' },
    { id: 'disguised-8',  deck: 'disguised', number: 8,  name: 'Planted Evidence',   effect: 'One enemy Overt character loses 1 action this turn (must investigate a false lead).' },
    { id: 'disguised-9',  deck: 'disguised', number: 9,  name: 'Double Agent',       effect: 'One of your Disguised characters may use their base role stats for one action this turn, ignoring their current persona.' },
    { id: 'disguised-10', deck: 'disguised', number: 10, name: 'Network',             effect: 'Gain 1 VP for each objective currently occupied by one of your Disguised characters.' },
    { id: 'disguised-11', deck: 'disguised', number: 11, name: 'Panic Button',        effect: 'Force one enemy Overt character to move 2 hexes directly away from your chosen Disguised character.' },
    { id: 'disguised-12', deck: 'disguised', number: 12, name: 'Impersonation',       effect: 'One of your Disguised characters may make one attack using their BS or MS as if they were Overt this turn.' },
  ],
};

// --- Per-player hand system ---

// A single deck's draw/discard state for hand dealing (separate from the round-card system).
export interface HandDeckState {
  pile: string[];    // card IDs available for dealing, top = index 0
  discard: string[]; // returned/played cards — reshuffled back when pile empties
}

export interface HandDecksState {
  overt: HandDeckState;
  hidden: HandDeckState;
  disguised: HandDeckState;
}

export interface PlayerHand {
  cards: string[];              // card IDs currently in hand
  activePlayedCard: string | null; // most recently played card, visible to all; replaced on next play
}

export function createHandDecks(rng: number): [HandDecksState, number] {
  let r = rng;
  const build = (deckId: HeistDeckId): HandDeckState => {
    const [pile, next] = shuffle(HEIST_CARDS[deckId].map(c => c.id), r);
    r = next;
    return { pile, discard: [] };
  };
  return [{ overt: build('overt'), hidden: build('hidden'), disguised: build('disguised') }, r];
}

// Draw `count` cards from a hand deck. Reshuffles discard into pile if needed.
export function dealFromHandDeck(deck: HandDeckState, count: number, rng: number): [string[], HandDeckState, number] {
  let r = rng;
  let pile = deck.pile;
  let discard = deck.discard;
  if (pile.length < count) {
    const combined = [...pile, ...discard];
    if (combined.length === 0) return [[], deck, r];
    const [reshuffled, next] = shuffle(combined, r);
    r = next;
    pile = reshuffled;
    discard = [];
  }
  const actualCount = Math.min(count, pile.length);
  return [pile.slice(0, actualCount), { pile: pile.slice(actualCount), discard }, r];
}

// Return cards to a hand deck's discard pile (they'll be reshuffled eventually).
export function returnToHandDeck(deck: HandDeckState, cards: string[]): HandDeckState {
  if (cards.length === 0) return deck;
  return { ...deck, discard: [...deck.discard, ...cards] };
}

// --- Flat lookup ---

// Flat lookup of every card by id.
export const HEIST_CARD_BY_ID: Record<string, HeistCard> = Object.values(HEIST_CARDS)
  .flat()
  .reduce((acc, card) => { acc[card.id] = card; return acc; }, {} as Record<string, HeistCard>);

export function getHeistCard(cardId: string | null): HeistCard | null {
  return cardId ? HEIST_CARD_BY_ID[cardId] ?? null : null;
}

// --- RNG (mulberry32) — returns [value, nextRng] so callers can thread state ---

function mulberry32Step(s: number): [number, number] {
  s = (s + 0x6d2b79f5) | 0;
  let t = Math.imul(s ^ (s >>> 15), 1 | s);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  const value = ((t ^ (t >>> 14)) >>> 0) / 2 ** 32;
  return [value, s];
}

// Fisher-Yates shuffle threaded through the seeded RNG. Returns the shuffled
// copy and the advanced rng seed.
function shuffle<T>(items: readonly T[], rng: number): [T[], number] {
  const arr = items.slice();
  let r = rng;
  for (let i = arr.length - 1; i > 0; i--) {
    const [value, next] = mulberry32Step(r);
    r = next;
    const j = Math.floor(value * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return [arr, r];
}

// --- Deck lifecycle ---

// Build the three decks, each shuffled, with empty discard and no active card.
// Threads and returns the advanced rng seed.
export function createHeistDecks(rng: number): [HeistDecksState, number] {
  let r = rng;
  const build = (deckId: HeistDeckId): HeistDeckState => {
    const [pile, next] = shuffle(HEIST_CARDS[deckId].map(c => c.id), r);
    r = next;
    return { drawPile: pile, discardPile: [], activeCardId: null };
  };
  return [
    { overt: build('overt'), hidden: build('hidden'), disguised: build('disguised') },
    r,
  ];
}

// Draw the top card of a single deck into its active slot. The previously
// active card is discarded. When the draw pile is empty, the discard pile is
// reshuffled back in first. Returns the new deck state and advanced rng seed.
export function drawFromDeck(deck: HeistDeckState, rng: number): [HeistDeckState, number] {
  let r = rng;
  let drawPile = deck.drawPile;
  let discardPile = deck.activeCardId ? [...deck.discardPile, deck.activeCardId] : deck.discardPile;

  if (drawPile.length === 0) {
    if (discardPile.length === 0) {
      // Nothing left to draw — leave the active slot empty.
      return [{ drawPile: [], discardPile: [], activeCardId: null }, r];
    }
    const [reshuffled, next] = shuffle(discardPile, r);
    r = next;
    drawPile = reshuffled;
    discardPile = [];
  }

  const [drawn, ...rest] = drawPile;
  return [{ drawPile: rest, discardPile, activeCardId: drawn }, r];
}

// --- State tally / majority resolution ---

export interface StateTally {
  Overt: number;
  Hidden: number;
  Disguised: number;
}

// Count characters across both teams in the three deck-triggering states.
// Stunned / Unconscious characters are not in any deck state and are ignored.
export function tallyStates(states: readonly CharacterState[]): StateTally {
  const tally: StateTally = { Overt: 0, Hidden: 0, Disguised: 0 };
  for (const s of states) {
    if (s === 'Overt') tally.Overt++;
    else if (s === 'Hidden') tally.Hidden++;
    else if (s === 'Disguised') tally.Disguised++;
  }
  return tally;
}

// Determine which deck(s) to draw from for the round, per the majority rule.
// Returns every deck tied for the highest count. If no character is in any
// deck state, returns an empty list.
export function majorityDecks(tally: StateTally): HeistDeckId[] {
  const counts: Record<HeistDeckId, number> = {
    overt: tally.Overt,
    hidden: tally.Hidden,
    disguised: tally.Disguised,
  };
  const max = Math.max(counts.overt, counts.hidden, counts.disguised);
  if (max === 0) return [];
  return HEIST_DECK_IDS.filter(id => counts[id] === max);
}
